import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { FiUploadCloud, FiDownload, FiTrash2, FiCheck, FiMoon, FiSun, FiLoader, FiClock, FiList } from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileImage } from 'react-icons/ai';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDarkMode } from '../context/DarkModeContext';
import { log, error, warn } from '../utils/logger';
import { conversionColorMapping, defaultTheme } from '../constants/colorMapping';
import SlideOutPanel from './SlideOutPanel';
import ConversionHistory from './ConversionHistory';
import { getOrCreateUserId as getUserId } from '../utils/uuid';
import { showToast } from '../utils/toast';

// ActionButton component matching FileUpload
const ActionButton = ({ icon, children, onClick, variant = "default" }) => {
  const { darkMode } = useDarkMode();
  const currentTheme = conversionColorMapping.pdfToImages || defaultTheme;
  
  const getThemeColor = () => {
    return currentTheme?.dragActive?.text || 'text-primary-500';
  };

  const baseClasses = "group flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium";
  
  const variants = {
    default: `
      ${darkMode
        ? 'bg-gray-900/90 border border-gray-700 text-neutral-100 shadow-sm hover:bg-gray-800 hover:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none'
        : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'}
    `,
    ghost: `
      ${darkMode
        ? 'bg-transparent text-neutral-400 hover:text-indigo-400 hover:bg-gray-800/60 focus:ring-2 focus:ring-indigo-500 focus:outline-none'
        : 'bg-transparent hover:bg-gray-100/50 text-gray-600'}
    `
  };

  return (
    <button
      onClick={onClick}
      className={`
        ${baseClasses} 
        ${variants[variant]}
        hover:shadow-sm
        active:scale-95
        transform transition-all duration-200
      `}
    >
      <span className={`
        inline-flex transition-all duration-200 transform
        group-hover:scale-110 group-hover:rotate-[-10deg]
        group-hover:${getThemeColor()}
        ${darkMode ? 'text-indigo-400' : ''}
      `}>
        {icon}
      </span>
      <span className={`
        transition-colors duration-200
        group-hover:${getThemeColor()}
      `}>
        {children}
      </span>
    </button>
  );
};

const PdfToImages = ({ setProgress }) => {
  const [file, setFile] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [result, setResult] = useState([]);
  const [zipUrl, setZipUrl] = useState(null);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [conversionToken, setConversionToken] = useState(null);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const [pdfInfo, setPdfInfo] = useState(null); // Store PDF metadata
  const [selectedPages, setSelectedPages] = useState([]); // For page selection
  const [imageFormat, setImageFormat] = useState('png'); // png or jpg
  const [dpi, setDpi] = useState(300); // Resolution

  const currentTheme = conversionColorMapping.pdfToImages || defaultTheme;

  useEffect(() => {
    const userId = getUserId();
    const storedHistory = JSON.parse(localStorage.getItem(`conversionHistory_${userId}`)) || [];
    setConversionHistory(storedHistory);
  }, []);

  const clearConversionHistory = () => {
    const userId = getUserId();
    setConversionHistory([]);
    localStorage.removeItem(`conversionHistory_${userId}`);
    showToast.success('Conversion history cleared.');
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles[0].type !== 'application/pdf') {
      toast.error('Please upload a PDF file.');
      return;
    }
    const droppedFile = acceptedFiles[0];
    setFile(droppedFile);
    setResult([]);
    setZipUrl(null);
    setConversionToken(null);
    setSelectedPages([]);
    
    // Try to extract PDF metadata (page count, etc.)
    // This is a client-side estimation - actual count comes from backend
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);
        const text = String.fromCharCode.apply(null, uint8Array.slice(0, 1000));
        // Try to find page count in PDF (rough estimate)
        const pageMatches = text.match(/\/Count\s+(\d+)/g);
        if (pageMatches) {
          const pageCount = parseInt(pageMatches[0].match(/\d+/)[0]);
          setPdfInfo({ 
            pageCount,
            fileName: droppedFile.name,
            fileSize: droppedFile.size 
          });
        }
      } catch (err) {
        // If we can't parse, that's okay - backend will provide info
        setPdfInfo({ 
          fileName: droppedFile.name,
          fileSize: droppedFile.size 
        });
      }
    };
    reader.readAsArrayBuffer(droppedFile);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop, 
    multiple: false,
    accept: { 'application/pdf': ['.pdf'] }
  });


  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a PDF file to convert.');
      return;
    }

    setIsConverting(true);

    try {
      const userId = getUserId();
      log(`Uploading file: ${file.name} for userId: ${userId}`);
      log('Requesting signed URL for upload:', file.name);

      const contentType = file.type || 'application/octet-stream';

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/getSignedUrl`, {
        fileName: file.name,
        userId,
        action: 'write',
        contentType,
        conversionType: 'pdfToImages'
      });

      const { signedUrl, publicUrl, token, contentType: returnedContentType } = response.data;
      log(`Received token from server: ${token}`);
      log('Received signed URL:', signedUrl);
      log('Content-Type:', returnedContentType);

      setConversionToken(token);

      // Upload the file to GCS using the signed URL
      await axios.put(signedUrl, file, {
        headers: {
          'Content-Type': returnedContentType,
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      log('File uploaded to GCS:', publicUrl);

      // Proceed with the conversion process using the public URL
      const conversionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversion/pdf-to-images`,
        {
          fileUrl: publicUrl,
          fileName: file.name,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      log('Conversion response:', conversionResponse.data);

      if (conversionResponse.data.fileUrls && conversionResponse.data.fileUrls.length > 0) {
        setResult(conversionResponse.data.fileUrls);
        setZipUrl(conversionResponse.data.zipUrl);
        
        // The backend should have already set the correct filename
        log('PDF converted successfully:', conversionResponse.data);
        
        // Save to history
        const newHistoryItem = {
          fileName: file.name,
          conversionType: 'pdfToImages',
          timestamp: new Date().toLocaleString(),
          token: conversionToken,
          imageCount: result.length
        };
        setConversionHistory(prev => {
          const updatedHistory = [...prev, newHistoryItem];
          localStorage.setItem(`conversionHistory_${userId}`, JSON.stringify(updatedHistory));
          return updatedHistory;
        });
        
        toast.success('PDF converted to images successfully!');
      } else {
        throw new Error('No converted files received from the server');
      }

    } catch (error) {
      error('Error converting PDF:', error);
      if (error.response) {
        error('Response data:', error.response.data);
        error('Response status:', error.response.status);
        error('Response headers:', error.response.headers);
      } else if (error.request) {
        error('No response received:', error.request);
      } else {
        error('Error setting up request:', error.message);
      }
      toast.error('Error converting PDF: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsConverting(false);
      setProgress(0);
    }
  };

  const handleDownload = async (url, fileName) => {
    log(`Preparing to download file: ${fileName} with token: ${conversionToken}`);
    try {
      log('Sending verification request for token:', conversionToken);
      const verificationResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversion/verifyToken`,
        { token: conversionToken },
        {
          headers: {
            'Authorization': `Bearer ${conversionToken}`
          }
        }
      );
      log('Token verification response:', verificationResponse.data);

      if (verificationResponse.data.valid) {
        log('Token verified successfully. Proceeding with download.');
        log('Starting file download');
        const response = await axios.get(url, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${conversionToken}`
          }
        });
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        log('File download completed');

        log('Download completed, finalizing token');
        const finalizationResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/conversion/finalizeToken`,
          { token: conversionToken },
          {
            headers: {
              'Authorization': `Bearer ${conversionToken}`
            }
          }
        );
        log('Token finalization response:', finalizationResponse.data);

        // Verify file deletion
        await verifyFileDeletion(conversionToken);

        toast.success(`${fileName} downloaded successfully!`);
      } else {
        error('Token verification failed');
        toast.error(`Error downloading ${fileName}: Invalid token`);
      }
    } catch (error) {
      error('Error downloading file:', error);
      toast.error(`Error downloading file: ${error.message}`);
    }
  };

  const verifyFileDeletion = async (token) => {
    try {
      log(`Verifying file deletion for token: ${token}`);
      const verificationResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversion/verifyDeletion/${token}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      log('File deletion verification response:', verificationResponse.data);
      
      if (verificationResponse.data.deletionSuccessful) {
        log('Files successfully deleted');
        toast.success('Files have been successfully deleted from the server.');
      } else {
        warn('File deletion may have failed. Details:', verificationResponse.data.details);
        toast.warn('File deletion may have been incomplete. Please check the console for details.');
      }

      // Check token status after verification
      await checkTokenStatus(token);

    } catch (error) {
      error('Error verifying file deletion:', error);
      if (error.response) {
        error('Error response:', error.response.data);
        error('Error status:', error.response.status);
      }
      toast.error(`Error verifying file deletion: ${error.message}`);
    }
  };

  const checkTokenStatus = async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/conversion/tokenStatus/${token}`);
      log('Token status after finalization:', response.data);
      // You can update the UI based on the status if needed
    } catch (error) {
      error('Error checking token status:', error);
    }
  };

  const location = useLocation();
  const baseUrl = 'https://iconvertify.com';
  // CRITICAL FIX: Canonical URL must ALWAYS match the current page URL
  const currentPath = location.pathname;
  const canonicalUrl = `${baseUrl}${currentPath}`;
  const pageTitle = 'Convert PDF to Images | Free Online PDF to JPG, PNG Converter - iconvertify';
  const description = 'Extract images from PDF files and convert them to JPG, PNG, or other image formats. Free, secure PDF to image conversion. No registration required.';
  const keywords = 'pdf to images, pdf to jpg, pdf to png, extract images from pdf, pdf converter, pdf image extractor';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* OpenGraph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={`${baseUrl}/favicon-assets/android-chrome-512x512.png`} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="Convert PDF to Images - iconvertify" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="iconvertify" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${baseUrl}/favicon-assets/android-chrome-512x512.png`} />
        <meta name="twitter:image:alt" content="Convert PDF to Images - iconvertify" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "PDF to Images Converter - iconvertify",
            "url": canonicalUrl,
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": description,
            "featureList": [
              "Extract images from PDF files",
              "Convert to JPG, PNG, and other formats",
              "Free conversion",
              "No registration required",
              "Secure file processing"
            ]
          })}
        </script>
      </Helmet>
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} shadow-md w-full max-w-4xl mx-auto mt-4 transition-colors duration-300`}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <ActionButton
              icon={<FiList className="w-4 h-4" />}
              onClick={() => setIsModalOpen(false)}
              variant="default"
            >
              View Conversions
            </ActionButton>
            
            <ActionButton
              icon={<FiClock className="w-4 h-4" />}
              onClick={() => setShowHistory(true)}
              variant="ghost"
            >
              History
            </ActionButton>
          </div>

          <ActionButton
            icon={darkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
            onClick={toggleDarkMode}
            variant="ghost"
          >
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </ActionButton>
        </div>

        <motion.div
          {...getRootProps()}
          className={`
            relative flex flex-col items-center justify-center w-full h-72 p-8
            ${darkMode ? 'bg-gray-900/90' : 'bg-white'}
            rounded-xl cursor-pointer transition-all duration-300 ease-out
            border-2 ${darkMode ? 'border-gray-800' : ''}
            shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)]
            hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)]
            hover:-translate-y-2 hover:scale-[1.01]
            ${darkMode ? 'bg-gradient-to-b from-gray-900/90 to-gray-950/80' : 'bg-gradient-to-b from-white to-gray-50/50'}
            active:scale-[0.99] active:shadow-[0_10px_40px_-12px_rgba(0,0,0,0.12)] active:translate-y-0
            ${isDragActive
              ? `border-solid ${currentTheme.dragActive.border} ${currentTheme.dragActive.background}`
              : `border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'} hover:border-solid hover:${currentTheme.dragActive.border}`
            }
          `}
          animate={{
            scale: isDragActive ? 1.02 : 1,
            y: isDragActive ? -8 : 0,
          }}
          whileHover={{
            y: -4,
            transition: { duration: 0.2 }
          }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20
          }}
        >
          <input {...getInputProps()} />
          
          {/* Sophisticated gradient background effect */}
          <div className={`absolute inset-0 rounded-xl transition-all duration-300 pointer-events-none
            ${darkMode 
              ? 'bg-gradient-to-br from-indigo-900/20 via-transparent to-indigo-800/10 opacity-0 group-hover:opacity-100' 
              : 'bg-gradient-to-br from-white via-indigo-50/20 to-indigo-100/30 opacity-0 group-hover:opacity-100'
            }`} />
          
          {/* Enhanced border glow with harmonious colors */}
          <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
                    transition-opacity duration-300 pointer-events-none
                    ${darkMode 
                      ? 'bg-gradient-to-r from-indigo-500/0 via-indigo-400/15 to-indigo-500/0' 
                      : 'bg-gradient-to-r from-indigo-200/0 via-indigo-300/12 to-indigo-200/0'
                    }
                    blur-xl`} />
          
          {/* Subtle inner glow for depth */}
          {isDragActive && (
            <div className={`absolute inset-[2px] rounded-xl pointer-events-none
              ${darkMode 
                ? 'bg-gradient-to-br from-indigo-600/10 via-indigo-500/5 to-transparent' 
                : 'bg-gradient-to-br from-indigo-100/20 via-indigo-50/10 to-transparent'
              }`} />
          )}

          <motion.div
            className="relative z-10 flex flex-col items-center gap-4"
            animate={{ 
              y: isDragActive ? -8 : 0,
              scale: isDragActive ? 1.1 : 1
            }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 20 
            }}
          >
            <FiUploadCloud 
              className={`h-16 w-16 transition-all duration-300
                ${isDragActive 
                  ? currentTheme?.dragActive?.text || 'text-indigo-500'
                  : currentTheme?.icon || 'text-gray-400'
                }
                drop-shadow-md hover:drop-shadow-xl
                ${darkMode ? 'drop-shadow-[0_2px_16px_rgba(99,102,241,0.15)]' : ''}`}
            />
            
            <p className={`
              text-lg text-center font-medium
              transition-all duration-300
              ${isDragActive 
                ? currentTheme.dragActive.text
                : darkMode ? 'text-neutral-100' : 'text-gray-600'
              }
              drop-shadow-md
              ${darkMode ? 'drop-shadow-[0_2px_8px_rgba(99,102,241,0.10)]' : ''}`}
            >
              {isDragActive ? "Drop the PDF here" : "Drag & drop a PDF here, or click to select"}
            </p>
          </motion.div>
          
          {/* Dark mode overlay */}
          {darkMode && (
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-gray-900/80 via-gray-950/60 to-gray-900/90 opacity-80 z-0" />
          )}

          {/* Enhanced animated particles with harmonious colors */}
          {isDragActive && (
            <motion.div
              className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Floating particles */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 rounded-full
                      ${darkMode ? 'bg-indigo-400/40' : 'bg-indigo-500/30'}
                      shadow-lg
                      ${darkMode ? 'shadow-indigo-500/30' : 'shadow-indigo-400/20'}`}
                    animate={{
                      y: [-25, 25, -25],
                      x: [Math.sin(i) * 15, Math.cos(i) * 15, Math.sin(i) * 15],
                      opacity: [0.4, 0.9, 0.4],
                      scale: [1, 1.3, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.25,
                    }}
                    style={{
                      left: `${15 + (i % 4) * 25}%`,
                      top: `${25 + Math.floor(i / 4) * 30}%`,
                    }}
                  />
                ))}
              </div>
              
              {/* Ripple effect */}
              <motion.div
                className={`absolute inset-0 rounded-xl
                  ${darkMode 
                    ? 'bg-gradient-radial from-indigo-500/10 via-indigo-400/5 to-transparent' 
                    : 'bg-gradient-radial from-indigo-300/15 via-indigo-200/8 to-transparent'
                  }`}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          )}
        </motion.div>

        <AnimatePresence>
          {file && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mt-6 p-4 rounded-lg shadow-sm flex items-center justify-between transition-all duration-200
                ${darkMode 
                  ? 'bg-gradient-to-r from-gray-800/90 to-gray-800/50 border border-gray-700/50' 
                  : 'bg-gradient-to-r from-white/95 to-white/80 border border-gray-200/50'
                }
                shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]`}
            >
              <div className="flex items-center space-x-4 flex-grow">
                <div className="flex-shrink-0 relative">
                  <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-indigo-200 dark:border-indigo-700 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30 flex items-center justify-center">
                    <AiOutlineFilePdf className="h-8 w-8 text-red-500" />
                  </div>
                </div>
                <div className="flex flex-col flex-grow min-w-0">
                  <span className={`font-medium truncate text-sm ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>
                    {file.name}
                  </span>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                      {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                    </span>
                    {pdfInfo?.pageCount && (
                      <span className={`text-xs ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} font-medium`}>
                        {pdfInfo.pageCount} {pdfInfo.pageCount === 1 ? 'page' : 'pages'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setFile(null)}
                className="p-1.5 w-8 h-7 flex items-center justify-center rounded-md transition-all duration-200
                  bg-rose-500 text-white hover:bg-rose-600
                  shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                  hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                  active:transform active:scale-[0.98]"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Conversion Options */}
        {file && !isConverting && result.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 p-4 rounded-lg border transition-all duration-200
              ${darkMode 
                ? 'bg-gray-800/50 border-gray-700/50' 
                : 'bg-gray-50/50 border-gray-200/50'
              }`}
          >
            <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-neutral-200' : 'text-gray-700'}`}>
              Conversion Options
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Image Format */}
              <div>
                <label className={`block text-xs font-medium mb-2 ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
                  Image Format
                </label>
                <select
                  value={imageFormat}
                  onChange={(e) => setImageFormat(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-sm border transition-all duration-200
                    ${darkMode 
                      ? 'bg-gray-700/50 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                    focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                >
                  <option value="png">PNG (Better Quality)</option>
                  <option value="jpg">JPG (Smaller Size)</option>
                </select>
              </div>
              
              {/* DPI Resolution */}
              <div>
                <label className={`block text-xs font-medium mb-2 ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
                  Resolution (DPI)
                </label>
                <select
                  value={dpi}
                  onChange={(e) => setDpi(parseInt(e.target.value))}
                  className={`w-full px-3 py-2 rounded-lg text-sm border transition-all duration-200
                    ${darkMode 
                      ? 'bg-gray-700/50 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                    focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                >
                  <option value="150">150 DPI (Standard)</option>
                  <option value="300">300 DPI (High Quality)</option>
                  <option value="600">600 DPI (Very High)</option>
                </select>
              </div>
            </div>
            {pdfInfo?.pageCount && pdfInfo.pageCount > 1 && (
              <div className="mt-3">
                <label className={`block text-xs font-medium mb-2 ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
                  Extract Pages (Leave empty for all pages)
                </label>
                <input
                  type="text"
                  placeholder={`e.g., 1,3,5-7 or leave empty for all ${pdfInfo.pageCount} pages`}
                  value={selectedPages.join(',')}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    if (value === '') {
                      setSelectedPages([]);
                    } else {
                      setSelectedPages(value.split(',').map(s => s.trim()));
                    }
                  }}
                  className={`w-full px-3 py-2 rounded-lg text-sm border transition-all duration-200
                    ${darkMode 
                      ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }
                    focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  Select specific pages: 1,3,5 or ranges: 1-5, or leave empty for all {pdfInfo.pageCount} pages
                </p>
              </div>
            )}
          </motion.div>
        )}

        <AnimatePresence>
          {result.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mt-6 p-4 rounded-lg shadow-sm transition-all duration-200
                ${darkMode 
                  ? 'bg-gradient-to-r from-gray-800/90 to-gray-800/50 border border-gray-700/50' 
                  : 'bg-gradient-to-r from-white/95 to-white/80 border border-gray-200/50'
                }
                shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]`}
            >
              <div className="mb-4">
                <p className={`${darkMode ? 'text-emerald-400' : 'text-emerald-700'} font-medium mb-2`}>
                  Conversion successful! ({result.length} {result.length === 1 ? 'image' : 'images'} extracted)
                </p>
                <p className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                  All images extracted from {file?.name || 'PDF'} • {imageFormat.toUpperCase()} format • {dpi} DPI
                </p>
              </div>
              {zipUrl && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setDownloadingFiles(prev => ({ ...prev, 'zip': true }));
                    handleDownload(zipUrl, 'converted_images.zip');
                  }}
                  disabled={downloadingFiles['zip']}
                  className="w-full mb-4 flex items-center justify-center gap-2 p-3 rounded-lg transition-all duration-200
                    bg-indigo-600 hover:bg-indigo-700 text-white
                    shadow-[0_2px_8px_-2px_rgba(99,102,241,0.3)] hover:shadow-[0_4px_12px_-2px_rgba(99,102,241,0.4)]
                    disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  {downloadingFiles['zip'] ? (
                    <FiLoader className="animate-spin w-5 h-5" />
                  ) : (
                    <FiDownload className="w-5 h-5" />
                  )}
                  <span className="font-medium">Download All as ZIP</span>
                </motion.button>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {result.map((url, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setDownloadingFiles(prev => ({ ...prev, [index]: true }));
                      handleDownload(url, `page_${index + 1}.${imageFormat}`);
                    }}
                    disabled={downloadingFiles[index]}
                    className={`flex flex-col items-center justify-center gap-2 p-3 rounded-lg transition-all duration-200
                      ${darkMode 
                        ? 'bg-gradient-to-br from-gray-700/90 to-gray-700/50 hover:from-gray-600 hover:to-gray-600 border border-gray-600/50' 
                        : 'bg-gradient-to-br from-white/95 to-white/80 hover:from-gray-50 hover:to-gray-50 border border-gray-200/50'
                      }
                      shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.1)]
                      disabled:opacity-75 disabled:cursor-not-allowed`}
                  >
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center
                      ${darkMode 
                        ? 'bg-indigo-900/30 border border-indigo-700/50' 
                        : 'bg-indigo-50 border border-indigo-200'
                      }`}>
                      {downloadingFiles[index] ? (
                        <FiLoader className="animate-spin w-5 h-5 text-indigo-500" />
                      ) : (
                        <AiOutlineFileImage className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                      )}
                    </div>
                    <div className="flex flex-col items-center">
                      <span className={`text-xs font-medium ${darkMode ? 'text-neutral-100' : 'text-gray-900'}`}>
                        Page {index + 1}
                      </span>
                      <span className={`text-[10px] ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        {imageFormat.toUpperCase()}
                      </span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <SlideOutPanel isOpen={showHistory} onClose={() => setShowHistory(false)} darkMode={darkMode}>
          <ConversionHistory
            history={conversionHistory}
            darkMode={darkMode}
            clearHistory={clearConversionHistory}
          />
        </SlideOutPanel>

        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          pauseOnHover
          theme={darkMode ? 'dark' : 'light'}
          className="!-translate-y-4"
          limit={5}
          closeButton={false}
        />
      </div>
    </>
  );
};

export default PdfToImages;